import { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import Layout from '../components/Layout';
import { useBranch } from '../contexts/BranchContext';
import { useAuth } from '../App';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Bed, UserPlus, Users, TrendUp, ClipboardText, Clock, Queue } from '@phosphor-icons/react';

// Convert 24-hour time to 12-hour AM/PM format
const convertTo12Hour = (time24) => {
  if (!time24) return '';
  
  const [hours, minutes] = time24.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
};

const Dashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [liveStatus, setLiveStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const { activeBranch } = useBranch();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const fetchAnalytics = useCallback(async () => {
    try {
      const response = await api.get('/analytics/dashboard', {
        params: { branch_id: activeBranch?.branch_id }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  }, [activeBranch]);

  const fetchLiveStatus = useCallback(async () => {
    try {
      const response = await api.get('/dashboard/live-status', {
        params: { branch_id: activeBranch?.branch_id }
      });
      setLiveStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch live status:', error);
    }
  }, [activeBranch]);

  useEffect(() => {
    if (activeBranch) {
      fetchAnalytics();
      fetchLiveStatus();
      
      // Auto-refresh live status every 10 seconds
      const interval = setInterval(fetchLiveStatus, 10000);
      return () => clearInterval(interval);
    }
  }, [activeBranch, fetchAnalytics, fetchLiveStatus]);

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#2C4C3B] border-r-transparent"></div>
        </div>
      </Layout>
    );
  }

  const stats = [
    {
      title: 'Total Customers',
      value: analytics?.total_customers || 0,
      icon: Users,
      color: 'text-[#2C4C3B]',
      bgColor: 'bg-[#2C4C3B]/10',
      testId: 'stat-total-customers'
    },
    {
      title: 'Today\'s Attendance',
      value: analytics?.today_attendance || 0,
      icon: ClipboardText,
      color: 'text-[#C05C42]',
      bgColor: 'bg-[#C05C42]/10',
      testId: 'stat-today-attendance'
    },
    {
      title: 'Active Check-ins',
      value: analytics?.active_checkins || 0,
      icon: Bed,
      color: 'text-[#2C4C3B]',
      bgColor: 'bg-[#2C4C3B]/10',
      testId: 'stat-active-checkins'
    },
    {
      title: 'Conversion Rate',
      value: `${analytics?.conversion_rate || 0}%`,
      icon: TrendUp,
      color: 'text-[#C05C42]',
      bgColor: 'bg-[#C05C42]/10',
      testId: 'stat-conversion-rate'
    }
  ];

  return (
    <Layout>
      <div data-testid="dashboard-page">
        <div className="mb-6 sm:mb-8">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl tracking-tight font-light text-[#2C4C3B] mb-2">Dashboard</h1>
              <p className="text-sm sm:text-base text-[#1C1C1A]/60">Overview of your therapy center operations</p>
            </div>
          </div>
        </div>

        {/* Live Status Widget */}
        {liveStatus && (
          <Card className="p-6 mb-8 bg-gradient-to-r from-green-50 to-blue-50 border-green-200" data-testid="live-status-widget">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
              <h2 className="text-xl font-semibold text-[#2C4C3B]">Live Status</h2>
              <span className="text-xs text-[#1C1C1A]/60 ml-auto">Updates every 10 seconds</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Current Batch Status */}
              <div className="bg-white rounded-lg p-6 border border-green-200">
                <div className="flex items-center gap-2 mb-4">
                  <Clock size={24} className="text-green-600" weight="fill" />
                  <h3 className="font-semibold text-[#2C4C3B]">Ongoing Batch</h3>
                </div>

                {liveStatus.current_batch ? (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-2xl font-bold text-[#2C4C3B]">
                        {convertTo12Hour(liveStatus.current_batch.start_time)} - {convertTo12Hour(liveStatus.current_batch.end_time)}
                      </span>
                      <Badge className="bg-green-600 text-white">Active</Badge>
                    </div>

                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-[#1C1C1A]/60">Occupied Beds:</span>
                        <span className="text-3xl font-bold text-[#2C4C3B]">
                          {liveStatus.current_batch.occupied}/{liveStatus.current_batch.capacity}
                        </span>
                      </div>

                      {/* Progress Bar */}
                      <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${
                            liveStatus.current_batch.occupancy_percent >= 100
                              ? 'bg-red-600'
                              : liveStatus.current_batch.occupancy_percent >= 80
                              ? 'bg-orange-500'
                              : 'bg-green-500'
                          }`}
                          style={{ width: `${Math.min(liveStatus.current_batch.occupancy_percent, 100)}%` }}
                        ></div>
                      </div>

                      <p className="text-sm text-center text-[#1C1C1A]/60">
                        {liveStatus.current_batch.occupancy_percent}% Occupancy
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-[#1C1C1A]/60">
                    <p>No active batch at the moment</p>
                    {liveStatus.next_batch && (
                      <p className="text-sm mt-2">
                        Next batch starts at {convertTo12Hour(liveStatus.next_batch.start_time)}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Next Batch Queue */}
              <div className="bg-white rounded-lg p-6 border border-orange-200">
                <div className="flex items-center gap-2 mb-4">
                  <Queue size={24} className="text-orange-600" weight="fill" />
                  <h3 className="font-semibold text-[#2C4C3B]">Next Batch Queue</h3>
                </div>

                <div className="text-center py-4">
                  <div className="text-6xl font-bold text-orange-600 mb-2">
                    {liveStatus.next_batch_waiting_count || 0}
                  </div>
                  <p className="text-sm text-[#1C1C1A]/60">
                    {liveStatus.next_batch_waiting_count === 1 ? 'Customer' : 'Customers'} waiting for next batch
                  </p>

                  {liveStatus.next_batch_waiting_count > 0 && liveStatus.next_batch && (
                    <div className="mt-4 p-3 bg-orange-50 rounded-lg border border-orange-200">
                      <p className="text-sm text-orange-800 font-medium">
                        ⏰ Next batch starts at {convertTo12Hour(liveStatus.next_batch.start_time)}
                      </p>
                    </div>
                  )}

                  {liveStatus.next_batch_waiting_count === 0 && (
                    <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
                      <p className="text-sm text-green-800 font-medium">
                        ✓ No queue for next batch
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Next Batch Waiting */}
        {liveStatus && liveStatus.next_batch_waiting_count > 0 && (
          <Card className="p-6 mb-8 bg-orange-50 border-orange-300" data-testid="next-batch-waiting-widget">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-orange-100 rounded-lg">
                  <Clock size={28} className="text-orange-600" weight="fill" />
                </div>
                <div>
                  <h2 className="text-2xl font-semibold text-[#2C4C3B]">
                    Waiting for Next Batch
                  </h2>
                  <p className="text-sm text-[#1C1C1A]/60">
                    Next Batch: {convertTo12Hour(liveStatus.next_batch?.start_time)} | {liveStatus.next_batch_waiting_count} members waiting
                  </p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-4xl font-bold text-orange-600">
                  {liveStatus.next_batch_waiting_count}
                </div>
                <div className="text-sm text-[#1C1C1A]/60">Members</div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
              {liveStatus.next_batch_waiting_customers?.slice(0, 6).map((customer, idx) => (
                <div 
                  key={idx} 
                  className="bg-white p-4 rounded-lg border-2 border-orange-300 hover:border-orange-500 hover:shadow-md transition-all"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Users size={20} className="text-orange-600" weight="fill" />
                      <span className="font-semibold text-[#2C4C3B]">
                        {customer.customer_name}
                      </span>
                    </div>
                    <Badge className="bg-orange-500 text-white">
                      #{customer.token_number}
                    </Badge>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-[#1C1C1A]/70">
                      <span className="w-16 font-medium">Bed:</span>
                      <span className="px-2 py-1 bg-orange-100 text-orange-900 rounded font-semibold">
                        #{customer.bed_number}
                      </span>
                    </div>
                    
                    {customer.customer_reg_no && (
                      <div className="flex items-center gap-2 text-sm text-[#1C1C1A]/70">
                        <span className="w-16 font-medium">Reg:</span>
                        <span>{customer.customer_reg_no}</span>
                      </div>
                    )}
                    
                    <div className="flex items-center gap-2 text-xs text-[#1C1C1A]/50 mt-2 pt-2 border-t border-orange-200">
                      <Clock size={14} />
                      <span>Waiting for {convertTo12Hour(liveStatus.next_batch?.start_time)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {liveStatus.next_batch_waiting_count > 6 && (
              <div className="mt-4 text-center">
                <p className="text-sm text-orange-800">
                  +{liveStatus.next_batch_waiting_count - 6} more customers waiting
                </p>
              </div>
            )}

            <div className="mt-4 p-4 bg-orange-100 rounded-lg border border-orange-300">
              <p className="text-sm text-orange-900">
                <strong>ℹ️ Note:</strong> These customers have checked in and are assigned to the next batch. They are waiting in the hall for their session to begin.
              </p>
            </div>
          </Card>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
          {stats.map((stat) => {
            const Icon = stat.icon;
            return (
              <Card
                key={stat.title}
                data-testid={stat.testId}
                className="p-4 sm:p-6 bg-white border border-[#E5E5DF] rounded-lg hover:-translate-y-1 transition-transform"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] font-semibold text-[#1C1C1A]/60 mb-2">
                      {stat.title}
                    </p>
                    <p className="text-2xl sm:text-3xl font-semibold tracking-tight text-[#2C4C3B]">{stat.value}</p>
                  </div>
                  <div className={`p-2 sm:p-3 rounded-lg ${stat.bgColor}`}>
                    <Icon size={20} weight="duotone" className={`sm:w-6 sm:h-6 ${stat.color}`} />
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="p-6 bg-white border border-[#E5E5DF] rounded-lg" data-testid="bed-occupancy-card">
            <h3 className="text-lg font-medium tracking-tight text-[#2C4C3B] mb-4">Bed Occupancy</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-[#1C1C1A]/70">Currently Active</span>
                <span className="text-lg font-semibold text-[#2C4C3B]">
                  {analytics?.beds_occupied || 0} / {analytics?.bed_capacity || 0}
                </span>
              </div>
              <div className="w-full bg-[#E2E8E4] rounded-full h-3">
                <div
                  className="bg-[#2C4C3B] h-3 rounded-full transition-all"
                  style={{
                    width: `${analytics?.bed_capacity > 0 ? ((analytics?.beds_occupied || 0) / analytics.bed_capacity) * 100 : 0}%`
                  }}
                ></div>
              </div>
            </div>
          </Card>

          <Card className="p-6 bg-white border border-[#E5E5DF] rounded-lg" data-testid="pipeline-card">
            <h3 className="text-lg font-medium tracking-tight text-[#2C4C3B] mb-4">Customer Pipeline</h3>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-[#1C1C1A]/70">Trial</span>
                <span className="text-base font-semibold text-[#2C4C3B]">{analytics?.trial_customers || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-[#1C1C1A]/70">Follow-up</span>
                <span className="text-base font-semibold text-[#C05C42]">{analytics?.followup_customers || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-[#1C1C1A]/70">Purchased</span>
                <span className="text-base font-semibold text-[#2C4C3B]">{analytics?.purchased_customers || 0}</span>
              </div>
            </div>
          </Card>

          <Card className="p-6 bg-white border border-[#E5E5DF] rounded-lg" data-testid="slots-card">
            <h3 className="text-lg font-medium tracking-tight text-[#2C4C3B] mb-4">Today&apos;s Slots</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-[#1C1C1A]/70">Occupied</span>
                <span className="text-lg font-semibold text-[#2C4C3B]">
                  {analytics?.occupied_slots || 0} / {analytics?.total_slots_today || 0}
                </span>
              </div>
              <div className="w-full bg-[#E2E8E4] rounded-full h-3">
                <div
                  className="bg-[#C05C42] h-3 rounded-full transition-all"
                  style={{
                    width: `${((analytics?.occupied_slots || 0) / (analytics?.total_slots_today || 1)) * 100}%`
                  }}
                ></div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;